import React, { useEffect } from 'react';
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import Button from '@common/button';
import Text from '@common/text';
import useProfileCareer, { useProfileCareerNHIS } from '@repository/profiles/useProfileCareer';
import JobGroup from '@components/profile/profileOverlay/overlays/career/careerForm/JobGroup';
import JobTitle from '@components/profile/profileOverlay/overlays/career/careerForm/JobTitle';
import ContractType from '@components/profile/profileOverlay/overlays/career/careerForm/ContractType';
import { NhisProfileCareerDto } from '@domain/rq/profile/NhisProfileCareerRq';
import NhisCompanyReportHistoryDto from '@domain/rs/nhis/NhisCompanyReportHistoryDto';
import { ReactSelectOption } from '@domain/constant/ReactSelectOption';
import { FontStyle } from '@domain/constant/fontStyle';
import Colors from '@domain/constant/colors';
import NumberFormat from '@utils/number/format';
import classnames from 'classnames/bind';
import styles from './index.module.scss';

const cx = classnames.bind(styles);

interface ListItemProps {
  index: number;
  control: any;
  company: FormType;
}

const ListItem = ({ index, control, company }: ListItemProps) => {
  const prefix = `careers[${index}]`;
  const companyName = company?.companyName || '';
  const endDateText = company?.endDate ? `${company?.endDate}` : '재직중';
  const dateText = `${company?.startDate} ~ ${endDateText}`;
  const salaryText = `${NumberFormat.format(company.totalSalary, '10K', ' ', ['', '만', '억'], (value) =>
    NumberFormat.formatDecimal(value),
  )}원`;

  const jobGroup = useWatch({ control, name: `${prefix}.jobGroup` });

  return (
    <div className={cx('itemArea')}>
      <Text fontStyle={FontStyle.B1_B} color={Colors.C_JOBDA_BLACK}>
        {companyName}
      </Text>
      <div className={cx('contentsArea')}>
        <Text fontStyle={FontStyle.B3_M} color={Colors.C_COOL_GRAY_70}>
          {dateText}
        </Text>
        <Text className={cx('salary')} fontStyle={FontStyle.B3_M} color={Colors.C_COOL_GRAY_70}>
          {salaryText}
        </Text>
      </div>
      <div className={cx('selectArea')}>
        <div className={cx('selectWrap')}>
          <Text className={cx('title')} fontStyle={FontStyle.B2_B} color={Colors.C_JOBDA_BLACK}>
            직군
          </Text>
          <div className={cx('select')}>
            <JobGroup name={`${prefix}.jobGroup`} />
          </div>
        </div>
        <div className={cx('selectWrap')}>
          <Text className={cx('title')} fontStyle={FontStyle.B2_B} color={Colors.C_JOBDA_BLACK}>
            직무
          </Text>
          <div className={cx('select')}>
            <JobTitle name={`${prefix}.jobTitle`} jobGroup={jobGroup} />
          </div>
        </div>
        <div className={cx('selectWrap')}>
          <Text className={cx('title')} fontStyle={FontStyle.B2_B} color={Colors.C_JOBDA_BLACK}>
            계약 형태
          </Text>
          <div className={cx('select')}>
            <ContractType name={`${prefix}.employmentType`} />
          </div>
        </div>
      </div>
    </div>
  );
};

type FormType = NhisCompanyReportHistoryDto & {
  jobGroup: ReactSelectOption | null;
  jobTitle: ReactSelectOption | null;
  employmentType: ReactSelectOption | null;
};

interface Step4Props {
  prevStep: () => void;
  nextStep: () => void;
  selectedCompanies: NhisCompanyReportHistoryDto[];
}

const Step6 = ({ prevStep, nextStep, selectedCompanies }: Step4Props) => {
  const [isDisabled, setIsDisabled] = React.useState(false);
  const { mutateAsync: mutateProfileCareer, isLoading } = useProfileCareerNHIS();
  const { refetch } = useProfileCareer(false);
  const careers = selectedCompanies.map((company: NhisCompanyReportHistoryDto) => ({
    ...company,
    jobGroup: null,
    jobTitle: null,
    employmentType: null,
  }));

  const methods = useForm<{ careers: FormType[] }>({ mode: 'onChange', defaultValues: { careers } });
  const { control, handleSubmit, getValues, watch } = methods;
  const { fields } = useFieldArray({ control, name: 'careers' });

  useEffect(() => {
    const isDisabled = watch('careers')?.some((company) => {
      return !company.jobGroup || !company.jobTitle || !company.employmentType;
    });
    setIsDisabled(isDisabled);
  }, [watch()]);
  console.log(
    getValues().careers?.map((company) => ({
      token: company.token,
      jobGroupCode: company.jobGroup?.value,
      jobTitleCode: company.jobTitle?.value,
      employmentType: company.employmentType?.value,
    })),
  );

  const onSubmit = async () => {
    if (isLoading) return;
    const careers = getValues().careers?.map((company) => ({
      token: company.token,
      jobGroupCode: company.jobGroup?.value,
      jobTitleCode: company.jobTitle?.value,
      employmentType: company.employmentType?.value,
    })) as NhisProfileCareerDto[];
    await mutateProfileCareer({ careers });
    await refetch();
    nextStep();
  };

  const els = fields
    ?.sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
    ?.map((field, index) => {
      const company = field as FormType;
      return <ListItem key={field.id} index={index} control={control} company={company} />;
    });

  return (
    <FormProvider {...methods}>
      <form className={cx('area')} onSubmit={handleSubmit(onSubmit)}>
        <div className={cx('wrap')}>
          <div className={cx('wrap')}>
            <em className={cx('desc')}>불러온 경력에 대한 필수 정보를 입력해 주세요.</em>

            <div className={cx('listArea')}>
              <div className={cx('listWrap')}>{els}</div>
            </div>
          </div>
        </div>

        <div className={cx('btnArea')}>
          <button className={cx('prevBtn')} onClick={prevStep}>
            이전
          </button>
          <Button className={cx('btn')} type={'submit'} disabled={isDisabled}>
            완료
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default Step6;
